import React, { useRef } from "react";
import clsx from "clsx";
import {
  SelectField,
  Typography,
  IconButton,
  FormItem,
  Box,
} from "@tiny/components";
import CloseBig from "@tiny/icons/generated/CloseBig";
import { AttributeTypeOption, useCondition } from "./Condition.hooks";
import { useStyles } from "./Condition.styles";
import { SegmentConditionsInfo } from "../../EditSegment.types";
import { DisabledAttributeTypes } from "../ConditionGroup.helpers";
import { BaseCondition } from "@tiny/api";
import { useFormContext } from "react-hook-form";

export interface ConditionProps {
  showDivider?: boolean;
  index: number;
  groupIndex: number;
  segmentConditionsInfo?: SegmentConditionsInfo;
  onDelete: (index: number) => void;
  prefixName: string;
  attributesTypes: NonNullable<BaseCondition["attributeType"]>[];
  /** Attribute types listed but not selectable on this row, with the reason. */
  disabledAttributeTypes?: DisabledAttributeTypes;
  disabled?: boolean;
  deletable?: boolean;
}

function isAttributeTypeOptionDisabled(option: AttributeTypeOption) {
  return Boolean(option.disabledReason);
}

// A disabled option ignores pointer events, so a hover tooltip would never
// open; the reason is shown as secondary text under the label instead.
function renderAttributeTypeOption(
  optionProps: React.HTMLAttributes<HTMLLIElement>,
  option: AttributeTypeOption
) {
  return (
    <li {...optionProps} key={option.value}>
      <Box display="flex" flexDirection="column">
        <span>{option.label}</span>
        {option.disabledReason && (
          <Typography variant="label3" color="grey" tone={600}>
            {option.disabledReason}
          </Typography>
        )}
      </Box>
    </li>
  );
}

function Condition(props: ConditionProps) {
  const { deletable = true } = props;
  const {
    ComponentValue,
    attributeNameOptions,
    attributeTypeOptions,
    attributeTypeRules,
    changeAttributeName,
    changeAttributeType,
    changeCriteria,
    criteriaOptions,
    deleteCondition,
    prefixName,
    shouldShowAttrType,
    shouldShowAttrName,
    shouldShowCriteria,
    shouldShowValue,
    showDivider,
    valueType,
    disabled,
    valueKey,
  } = useCondition(props);

  const classes = useStyles();

  const valueClassName = clsx(classes.valueTwo, {
    [classes.valueThree]: attributeNameOptions.length === 1,
    [classes.valueFour]: attributeTypeOptions.length === 1,
  });

  const { trigger } = useFormContext();
  const triggerRef = useRef(trigger);

  React.useEffect(() => {
    triggerRef.current?.();
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.main}>
        <div className={classes.fields}>
          {shouldShowAttrType && (
            <div className={classes.attributeType}>
              <FormItem
                name={`${prefixName}.attributeType`}
                onChangeValue={changeAttributeType}
                rules={attributeTypeRules}
              >
                <SelectField
                  options={attributeTypeOptions}
                  aria-label="Condition type"
                  showHelperTextMargin
                  disabled={disabled}
                  placeholder="Choose filter"
                  getOptionDisabled={isAttributeTypeOptionDisabled}
                  renderOption={renderAttributeTypeOption}
                />
              </FormItem>
            </div>
          )}
          {shouldShowAttrName && (
            <div className={classes.attributeName}>
              <FormItem
                name={`${prefixName}.attributeName`}
                onChangeValue={changeAttributeName}
                rules={{
                  required: {
                    value: true,
                    message: "Required field",
                  },
                }}
              >
                <SelectField
                  options={attributeNameOptions}
                  aria-label="Condition name"
                  showHelperTextMargin
                  disabled={disabled}
                  placeholder="Choose filter"
                />
              </FormItem>
            </div>
          )}
          {shouldShowCriteria && (
            <div className={classes.criteria}>
              <FormItem
                name={`${prefixName}.criteria`}
                onChangeValue={changeCriteria}
                rules={{
                  required: {
                    value: true,
                    message: "Required field",
                  },
                }}
              >
                <SelectField
                  options={criteriaOptions}
                  aria-label="Condition criteria"
                  showHelperTextMargin
                  disabled={disabled}
                  placeholder="Choose filter"
                />
              </FormItem>
            </div>
          )}
          {shouldShowValue && (
            <div className={valueClassName}>
              <ComponentValue
                key={valueKey}
                type={valueType}
                prefixName={prefixName}
                disabled={disabled}
              />
            </div>
          )}
        </div>
        <div className={classes.delete}>
          {deletable && (
            <IconButton
              className={classes.deleteButton}
              onClick={deleteCondition}
              disabled={disabled}
              size="large"
            >
              <CloseBig />
            </IconButton>
          )}
        </div>
      </div>
      {showDivider && (
        <div className={classes.divider}>
          <Typography variant="label3" color="grey" tone={600}>
            AND
          </Typography>
        </div>
      )}
    </div>
  );
}

export default React.memo(Condition);
